import type { DevicePublicJwk } from '@fas/types';

/*
 * This browser's device identity (§37 device metadata and device registration, Phase 2B,
 * ADR-043). A non-extractable ECDSA P-256 key is created once and kept in IndexedDB; its
 * RFC 7638 thumbprint is the device id sent with every request, and the key signs device
 * proofs for registration and clock events. It identifies the browser installation, not the
 * person, and cannot be copied to another browser.
 */

const ID_KEY = 'fas.deviceId';
const DB_NAME = 'fas-device';
const STORE = 'keys';
const RECORD = 'device';
/** Requests wait at most this long for the key at start-up before using the stored id. */
const READY_TIMEOUT_MS = 1_500;

export interface DeviceKey {
  keyId: string;
  publicKey: DevicePublicJwk;
  privateKey: CryptoKey;
}

let current: string | null = null;
let loading: Promise<DeviceKey | null> | null = null;

function randomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** Device id for request headers: the key's thumbprint once known, else a stored random id. */
export function getDeviceId(): string {
  if (current) return current;
  try {
    let id = localStorage.getItem(ID_KEY);
    if (!id) {
      id = randomId();
      localStorage.setItem(ID_KEY, id);
    }
    return id;
  } catch {
    return 'unavailable';
  }
}

export function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** RFC 7638 JWK thumbprint (SHA-256 over crv, kty, x, y in this order), base64url. */
export async function thumbprint(jwk: DevicePublicJwk): Promise<string> {
  const canonical = `{"crv":"${jwk.crv}","kty":"${jwk.kty}","x":"${jwk.x}","y":"${jwk.y}"}`;
  return base64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonical))));
}

function supported(): boolean {
  return typeof indexedDB !== 'undefined' && typeof crypto !== 'undefined' && !!crypto.subtle;
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function openDb(): Promise<IDBDatabase> {
  const req = indexedDB.open(DB_NAME, 1);
  req.onupgradeneeded = () => req.result.createObjectStore(STORE);
  return request(req);
}

async function readKey(db: IDBDatabase): Promise<DeviceKey | undefined> {
  return request(db.transaction(STORE, 'readonly').objectStore(STORE).get(RECORD)) as Promise<DeviceKey | undefined>;
}

async function createKey(db: IDBDatabase): Promise<DeviceKey> {
  const pair = await crypto.subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign', 'verify']);
  const jwk = await crypto.subtle.exportKey('jwk', pair.publicKey);
  const publicKey: DevicePublicJwk = { kty: 'EC', crv: 'P-256', x: jwk.x!, y: jwk.y! };
  const key: DeviceKey = { keyId: await thumbprint(publicKey), publicKey, privateKey: pair.privateKey };
  try {
    // `add` fails if another tab stored a key first; that key then wins everywhere.
    await request(db.transaction(STORE, 'readwrite').objectStore(STORE).add(key, RECORD));
    return key;
  } catch {
    return (await readKey(db)) ?? key;
  }
}

/** Loads (or on first use creates) this browser's device key; null where browsers cannot keep one. */
export function loadDeviceKey(): Promise<DeviceKey | null> {
  loading ??= (async () => {
    if (!supported()) return null;
    try {
      const db = await openDb();
      const key = (await readKey(db)) ?? (await createKey(db));
      db.close();
      current = key.keyId;
      try {
        localStorage.setItem(ID_KEY, key.keyId);
      } catch {
        // Storage may be blocked; the id still works for this page.
      }
      return key;
    } catch {
      return null;
    }
  })();
  return loading;
}

/** Device id for a request: waits briefly for the key at start-up so the first requests carry it. */
export async function readyDeviceId(): Promise<string> {
  if (!current && loading) {
    await Promise.race([loading, new Promise((resolve) => setTimeout(resolve, READY_TIMEOUT_MS))]);
  }
  return getDeviceId();
}

/** Test hook: forget the cached key (the stored one stays). */
export function resetDeviceKeyForTests(): void {
  current = null;
  loading = null;
}
